import { Injectable } from '@angular/core';
import { BackendService } from '@tg4/http-infrastructure/dist/src';
import { Observable } from 'rxjs';
import { ProcessTypeEnum } from '../shared/interfaces/process.interface';
import { PerformanceCycle } from './_interfaces/performance-cycle.interface';
import { isNil } from "lodash";
import { CycleProcess } from '../shared/interfaces/cycle.interface';

@Injectable()
export class PerformanceCycleService {

    constructor(private _httpService: BackendService) {
    }

    public getPeformanceCycles(year: number, cycleId: number = null,
        directSubordinates = false, userStatus = null, attributeIds: number[] = null): Observable<any> {
        const parameters = [
            { 'name': 'year', 'value': year as any },
            { 'name': 'cycleId', 'value': cycleId as any },
            { 'name': 'directSubordinates', 'value': directSubordinates },
            { 'name': 'userStatus', 'value': userStatus }
        ];

        if (attributeIds)
            parameters.push(...attributeIds.map(x => <any>{ 'name': 'attributeIds', 'value': x }));

        return this._httpService.get('getPerformanceCycles', [], parameters.filter(arg => !isNil(arg.value)));
    }

    public getPerformanceStepsFromCycleByYear(year: number, cycleId: number = null,
        directSubordinates = false, userStatus = null, attributeIds: number[] = null): Observable<any> {
        const parameters = [
            { 'name': 'year', 'value': year as any },
            { 'name': 'cycleId', 'value': cycleId as any },
            { 'name': 'directSubordinates', 'value': directSubordinates },
            { 'name': 'userStatus', 'value': userStatus }
        ];

        if (attributeIds)
            parameters.push(...attributeIds.map(x => <any>{ 'name': 'attributeIds', 'value': x }));

        return this._httpService.get('getPerformanceStepsFromCycleByYear', [], parameters.filter(arg => !isNil(arg.value)));
    }

    public getCycleById(cycleId: number): Observable<any> {
        return this._httpService.get('getCycleById', [], [
            { 'name': 'id', 'value': cycleId as any }
        ]);
    }

    public addCycle(cycle: PerformanceCycle): Observable<any> {
        delete cycle.id;

        if (typeof cycle.year === 'string') {
            const year = new Date(cycle.year);
            cycle.year = year.getFullYear();
        }

        return this._httpService.post('addCycle', cycle);
    }

    public updateCycle(cycle: PerformanceCycle): Observable<any> {
        return this._httpService.put('updateCycle', cycle);
    }

    public impersonateUser(userId: string): Observable<any> {
        return this._httpService.post('impersonateUser',
            {
                'UserId': userId
            });
    }

    public leaveImpersonation(): Observable<any> {
        return this._httpService.get('leaveImpersonation');
    }

    public updateCycleParticipants(cycleId: number, userIds: Array<string>): Observable<any> {
        return this._httpService.put('updateCycleParticipants', {
            'id': cycleId,
            'userIds': userIds
        });
    }

    public getCycleSubordinates(
        processId: number, cycleProcessConfigId: number, cycleId: number,
        page: number = 1, pageSize: number = 40, searchValue: string = '', directSubordinates = false,
        userStatus: number = null, attributeIds: number[] = null
    ): Observable<any> {
        const parameters = [
            { 'name': 'cycleId', 'value': cycleId as any },
            { 'name': 'processId', 'value': processId as any },
            { 'name': 'cycleProcessConfigId', 'value': cycleProcessConfigId as any },
            { 'name': 'page', 'value': page as any },
            { 'name': 'pageSize', 'value': pageSize as any },
            { 'name': 'search', 'value': searchValue },
            { 'name': 'directSubordinates', 'value': directSubordinates },
            { 'name': 'userStatus', 'value': userStatus }
        ];

        if (attributeIds)
            parameters.push(...attributeIds.map(x => <any>{ 'name': 'attributeIds', 'value': x }));

        return this._httpService.get('getCycleSubordinates', [], parameters.filter(arg => !isNil(arg.value)));
    }

    public getUsersList(
        cycleId: number, page: number = 1, pageSize: number = 10, searchValue: string = ''
        , allUsers: boolean = false, attributeIds: Array<number> = []): Observable<any> {

        const parameters = [
            { 'name': 'cycleId', 'value': cycleId as any },
            { 'name': 'searchString', 'value': searchValue },
            { 'name': 'page', 'value': page as any },
            { 'name': 'pageSize', 'value': pageSize as any },
            { 'name': 'allUsers', 'value': allUsers as any },
            { 'name': 'attributeIds', 'value': attributeIds as any }
        ];

        if (attributeIds && attributeIds.length > 0)
            parameters.push(...attributeIds.map(x => <any>{ 'name': 'attributeIds', 'value': x }));

        return this._httpService.get('getUsersList', [], parameters);
    }

    public getLastCycles(): Observable<any> {
        return this._httpService.get('getLastCycles', [], []);
    }

    public getAllCycleParticipants(cycleId: number): Observable<any> {
        return this._httpService.get('getAllCycleParticipants', [], [
            { 'name': 'cycleId', 'value': cycleId as any }
        ]);
    }

    public enableStepForUser(
        processType: ProcessTypeEnum, cycleParticipantId: number, cycleProcessConfigId: number, closed: Boolean
    ): Observable<any> {
        return this._httpService.post('moveUser', {
            'cycleParticipantId': cycleParticipantId,
            'processType': processType,
            'cycleProcessConfigId': cycleProcessConfigId,
            'closed': closed
        });
    }

    public enableStepForAllUsers(
        processType: ProcessTypeEnum, cycleId: number, cycleProcessConfigId: number, closed: Boolean
    ): Observable<any> {
        return this._httpService.post(
            'openCloseProcessForAllUsersCycle', {
            'cycleId': cycleId,
            'processType': processType,
            'closed': closed,
            'cycleProcessConfigId': cycleProcessConfigId,
            'open': true
        });
    }

    public closeProcessForAllUsers(
        processType: ProcessTypeEnum, cycleId: number, cycleProcessConfigId: number, closed: Boolean
    ): Observable<any> {
        return this._httpService.post(
            'openCloseProcessForAllUsersCycle', {
            'cycleId': cycleId,
            'processType': processType,
            'closed': closed,
            'cycleProcessConfigId': cycleProcessConfigId,
            'open': false
        }
        );
    }

    public closeProcessForUser(
        processType: ProcessTypeEnum, cycleParticipantId: number, cycleProcessConfigId: number, closed: Boolean
    ): Observable<any> {
        return this._httpService.post(
            this._getCloseUrlKeyByProcess(processType), {
            'cycleParticipantId': cycleParticipantId,
            'processType': processType,
            'cycleProcessConfigId': cycleProcessConfigId,
            'closed': closed
        }
        );
    }

    public reopenProcessForUser(
        processType: ProcessTypeEnum, cycleParticipantId: number, cycleProcessConfigId: number, closed: boolean, isFromUserList: boolean
    ): Observable<any> {
        return this._httpService.post(
            this._getReopenUrlKeyByProcess(processType), {
            'cycleParticipantId': cycleParticipantId,
            'processType': processType,
            'cycleProcessConfigId': cycleProcessConfigId,
            'closed': closed,
            'isFromUserList': isFromUserList
        });
    }

    public validateProcesses(processes: Array<CycleProcess>): string | null {
        if (processes.length === 0)
            return 'performanceCycle.cycleWithoutProcesses';

        for (let index = 0; index < processes.length; index++) {
            const process = processes[index];

            if (!process.included)
                continue;

            if (!process.processId) {
                return 'performanceCycle.versionNotDefined';
            }

            if (!process.startDate || !process.endDate) {
                return 'performanceCycle.datesNotDefined';
            }

            if (
                process.type === ProcessTypeEnum.GoalInputs ||
                process.type === ProcessTypeEnum.GoalsReview
            ) {
                if (!process.validationStartDate || !process.validationEndDate) {
                    return 'performanceCycle.datesNotDefined';
                }
            }
        }

        return null;
    }

    private _getCloseUrlKeyByProcess(processType: ProcessTypeEnum): string {
        switch (processType) {
            case ProcessTypeEnum.GoalInputs:
                return 'closeGoalsInputForUser';
            case ProcessTypeEnum.TwoPlusTwoCoaching:
                return 'closeTwoPlusTwoCoachingForUser';
            case ProcessTypeEnum.FinalFeedback:
                return 'closeFinalFeedbackForUser';
            case ProcessTypeEnum.FreeEvaluationMidYear:
                return 'closeFreeEvaluationMidYearForUser';
            case ProcessTypeEnum.GoalsReview:
                return 'closeGoalsReviewForUser';
            case ProcessTypeEnum.FreeEvaluationEndYear:
                return 'finishStep';
            default:
                return 'finishStep';
        }
    }

    private _getReopenUrlKeyByProcess(processType: ProcessTypeEnum): string {
        switch (processType) {
            // case ProcessTypeEnum.GoalInputs:
            //   return 'reopenGoalsInputForUser';
            // case ProcessTypeEnum.TwoPlusTwoCoaching:
            //   return 'reopenTwoPlusTwoCoachingForUser';
            // case ProcessTypeEnum.FreeEvaluationMidYear:
            //   return 'reopenFreeEvaluationMidYearForUser';
            case ProcessTypeEnum.GoalInputValidation:
                return 'reopenGoalInputValidationForCycle';
            case ProcessTypeEnum.GoalsReviewValidation:
                return 'reopenGoalsReviewValidationForUser';
            case ProcessTypeEnum.ThreeSixtyEvaluatorsValidation:
                return 'reopenThreeSixtyEvaluatorsValidationForUser';
            default:
                return 'moveUser';
        }
    }
}
